/**
 * @fileoverview Mission Section - "On a mission to eliminate write-offs"
 *
 * Purpose: Bridge section between the feature cards and the data insights
 * section. Centered serif headline above a tabbed customer testimonial.
 *
 * Layout:
 * - Centered headline.
 * - A bordered testimonial card that auto-rotates through customer quotes as a
 *   carousel. A row of headline stats sits on top as (non-interactive) progress
 *   indicators, highlighting whichever testimonial is currently showing.
 *
 * Theming: uses the global semantic CSS variables so the section adapts to
 * light and dark mode (the marketing footer exposes a theme switcher).
 *
 * Used by: Homepage (index.tsx)
 */

import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { cn } from "@/utils/misc";

/** How long each testimonial is shown before the carousel advances. */
const CAROUSEL_INTERVAL_MS = 6000;

/** Serif font stack matching the global heading font (see index.css). */
const SERIF_FONT = "'Lora', serif";

/**
 * A headline outcome statistic shown in the static stat row above the
 * testimonial carousel. Stats are independent of the testimonials.
 */
interface Stat {
  /** Value the stat counts from when scrolled into view. */
  from: number;
  /** The headline figure the stat counts to, e.g. 90. */
  to: number;
  /** Formats the (animated) numeric value for display, e.g. (v) => `90%`. */
  format: (value: number) => string;
  /** Short qualifier shown beside the stat. */
  statLabel: string;
}

/** Static headline outcomes shown above the testimonial carousel. */
const STATS: Stat[] = [
  {
    from: 0,
    to: 90,
    format: (v) => `${Math.round(v)}%`,
    statLabel: "average reduction in write-offs",
  },
  {
    from: 320,
    to: 11,
    // Counts down in hours, then lands on the 11-minute punchline.
    format: (v) => (v < 12 ? "11 mins" : `${Math.round(v)} hrs`),
    statLabel: "total time to complete an investigation",
  },
  {
    from: 300,
    to: 2,
    format: (v) => `${Math.round(v)} mins`,
    statLabel: "to generate Day Ones",
  },
];

/**
 * A single customer testimonial shown in the carousel. Add as many as needed;
 * the carousel auto-rotates and renders one navigation dot per entry.
 */
interface Testimonial {
  /** The quote body. */
  quote: string;
  /** Person being quoted. */
  name: string;
  /** Their role / title (optional). */
  role?: string;
  /** Company wordmark shown on the right. */
  company: string;
  /** Optional company logo image — rendered instead of the text wordmark. */
  logo?: string;
  /** Optional dark-mode variant of the logo (light lettering). */
  logoDark?: string;
  /**
   * Tailwind height class for the logo, tuned per brand so wide lockups read at
   * the same visual weight as compact monograms. Defaults to `h-8`.
   */
  logoClassName?: string;
}

/** Customer testimonials rotated through the carousel. */
const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "The significance of AI-driven services to our industry cannot be underestimated. Without positive and practical engagement, the way in which we currently undertake many of our processes will become obsolete. Morgan allows us the efficiency to streamline our operations, the flexibility to operate at scale across broad data-sets and to bring along our team at an accelerated rate on the true practical aspects of what it means to be in insolvency practice. It is a game-changer.",
    name: "Tony Lane",
    role: "Owner, Registered Liquidator",
    company: "Beacon Advisory",
    logo: "/logos/beacon.png",
    logoDark: "/logos/beacon-dark.png",
    logoClassName: "h-12",
  },
  {
    quote:
      "Morgan provides a platform that streamlines our efficiencies and enhances our workflow allowing us to focus on the judgement calls that require human interaction. This will be a gamechanger for our firm and the industry and we look forward to continuing to work with the Morgan team.",
    name: "Benjamin Ho",
    role: "Partner, Registered Liquidator",
    company: "WLP Restructuring",
    logo: "/logos/wlpr.png",
    logoDark: "/logos/wlpr-dark.png",
    // Very wide lockup — smaller height keeps it at the same visual weight.
    logoClassName: "h-6",
  },
  {
    quote:
      "The game is changing. Speed and accuracy are key when dealing with an insolvency estate. Stakeholders demand information. As a consequence, I need accurate answers fast. Morgan enables me to pull up any document, summary, or analysis in seconds. What used to take my admin team hours now happens instantly, and that's a game changer.",
    name: "Eddie Senatore",
    role: "Founder, Registered Liquidator",
    company: "ES",
    logo: "/logos/es.png",
    logoDark: "/logos/es-dark.png",
  },
  {
    quote:
      "Morgan significantly enhances the quality and efficiency of our work. It enables us to interrogate large volumes of financial data quickly, identify anomalies earlier, and focus on the areas of highest risk. This strengthens the robustness of our reporting and supports more timely, informed decision-making across engagements. It is a powerful tool that improves investigative outcomes and will help lift the standard of the industry.",
    name: "Oliver Grieves",
    role: "Supervisor",
    company: "Beacon Advisory",
    logo: "/logos/beacon.png",
    logoDark: "/logos/beacon-dark.png",
    logoClassName: "h-12",
  },
];

/**
 * AnimatedStat — counts from `from` to `to` when `start` flips true (the card
 * scrolls into view), then holds. Honours prefers-reduced-motion by rendering
 * the final figure.
 */
function AnimatedStat({
  from,
  to,
  format,
  start,
}: {
  from: number;
  to: number;
  format: (value: number) => string;
  /** Begin counting once true (driven by the card's in-view state). */
  start: boolean;
}) {
  const reducedMotion = useReducedMotion() ?? false;
  const [value, setValue] = useState(reducedMotion ? to : from);

  useEffect(() => {
    if (!start) return;
    if (reducedMotion) {
      setValue(to);
      return;
    }
    const controls = animate(from, to, {
      duration: 1.4,
      ease: "easeOut",
      onUpdate: setValue,
    });
    return () => controls.stop();
  }, [start, reducedMotion, from, to]);

  return (
    <span
      className="whitespace-nowrap text-2xl leading-none md:text-[28px]"
      style={{ fontFamily: SERIF_FONT }}
    >
      {format(value)}
    </span>
  );
}

/** Derive up-to-two-letter initials for the avatar fallback. */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Customer testimonial card.
 *
 * A static row of headline stats sits on top (count-up on scroll into view).
 * Below, an auto-rotating carousel cycles through the testimonials — pausing
 * while hovered, crossfading each quote/attribution and exposing one navigation
 * dot per testimonial. Honours prefers-reduced-motion by swapping instantly.
 */
function TestimonialCard() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const reducedMotion = useReducedMotion() ?? false;
  const active = TESTIMONIALS[activeIndex];

  // Trigger every stat count-up together when the card scrolls into view.
  const cardRef = useRef<HTMLDivElement>(null);
  const statsInView = useInView(cardRef, { once: true, amount: 0.4 });

  // Auto-advance the carousel. Pauses on hover so the reader can dwell on a
  // quote; resumes from the current slide once the pointer leaves.
  useEffect(() => {
    if (TESTIMONIALS.length <= 1) return;
    if (isPaused) return;
    const id = setInterval(() => {
      setActiveIndex((current) => (current + 1) % TESTIMONIALS.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isPaused]);

  return (
    <div ref={cardRef} className="mt-16 border border-primary/10">
      {/* Static headline stats (independent of the testimonial carousel) */}
      <div className="grid grid-cols-1 border-b border-primary/10 md:grid-cols-3">
        {STATS.map((stat, index) => (
          <div
            key={stat.statLabel}
            className={cn(
              "flex min-h-[72px] flex-col items-start justify-center gap-1 border-b border-primary/10 px-6 py-3 text-left text-foreground last:border-b-0 md:h-[90px] md:min-h-0 md:border-b-0 md:border-l md:border-l-primary/10 md:py-0 md:first:border-l-0",
              index === 0 && "md:border-l-0",
            )}
          >
            <AnimatedStat
              from={stat.from}
              to={stat.to}
              format={stat.format}
              start={statsInView}
            />
            <span className="text-sm text-foreground/50">{stat.statLabel}</span>
          </div>
        ))}
      </div>

      {/* Testimonial carousel (auto-rotates; pauses on hover) */}
      <div
        className="px-8 py-10 md:px-12 md:py-12"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div aria-live="polite" aria-atomic="true">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: reducedMotion ? 0 : 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
              transition={{
                duration: reducedMotion ? 0 : 0.4,
                ease: "easeOut",
              }}
            >
              <blockquote
                className="max-w-5xl text-xl leading-relaxed text-foreground md:text-2xl"
                style={{ fontFamily: SERIF_FONT }}
              >
                &ldquo;{active.quote}&rdquo;
              </blockquote>

              {/* Attribution + company wordmark */}
              <div className="mt-10 flex items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                    {getInitials(active.name)}
                  </span>
                  <span className="leading-tight">
                    <span className="block text-sm font-semibold text-foreground">
                      {active.name}
                    </span>
                    {active.role && (
                      <span className="block text-sm text-foreground/50">
                        {active.role}
                      </span>
                    )}
                  </span>
                </div>
                {active.logo ? (
                  <>
                    <img
                      src={active.logo}
                      alt={active.company}
                      className={cn(
                        "w-auto shrink-0 object-contain",
                        active.logoClassName ?? "h-8",
                        active.logoDark && "dark:hidden",
                      )}
                    />
                    {active.logoDark && (
                      <img
                        src={active.logoDark}
                        alt={active.company}
                        className={cn(
                          "hidden w-auto shrink-0 object-contain dark:block",
                          active.logoClassName ?? "h-8",
                        )}
                      />
                    )}
                  </>
                ) : (
                  <span
                    className="text-xl tracking-tight text-foreground/70"
                    style={{ fontFamily: SERIF_FONT }}
                  >
                    {active.company}
                  </span>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Carousel navigation dots */}
        {TESTIMONIALS.length > 1 && (
          <div className="mt-8 flex items-center gap-2">
            {TESTIMONIALS.map((testimonial, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={testimonial.name}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show testimonial from ${testimonial.name}`}
                  aria-current={isActive}
                  className={cn(
                    "h-2 rounded-full transition-all",
                    isActive
                      ? "w-6 bg-primary"
                      : "w-2 bg-primary/20 hover:bg-primary/40",
                  )}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Mission section with a centered headline and three animation containers.
 */
export function MissionSection() {
  return (
    <section className="relative w-full border-t border-primary/10 bg-background py-8 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="mx-auto max-w-2xl text-center text-3xl font-medium leading-[1.15] md:text-[42px]">
          Real time saved. Real value recovered.
        </h2>

        {/* Tabbed customer testimonial */}
        <TestimonialCard />
      </div>
    </section>
  );
}
